
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export interface SubscriptionData {
  id: string;
  status: string;
  current_period_end: string;
  plan: {
    name: string;
    amount: number;
    interval: string;
  };
}

export function useStripeSubscription() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSubscription = async (businessId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-subscription/get', {
        body: { businessId }
      });
      
      if (error) {
        console.error("Error fetching subscription:", error);
        return null;
      }
      
      if (data && data.subscription) {
        return data.subscription.id;
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching subscription:", error);
      return null;
    }
  };
  
  const getSubscriptionDetails = async (businessId: string): Promise<SubscriptionData | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-subscription/get', {
        body: { businessId }
      });
      
      if (error) {
        console.error("Error fetching subscription details:", error);
        return null;
      }
      
      if (data && data.subscription) {
        return data.subscription as SubscriptionData;
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching subscription details:", error);
      return null;
    }
  };
  
  const cancelSubscription = async (businessId: string, subscriptionId: string) => {
    try {
      setIsSubmitting(true);
      
      if (!subscriptionId) {
        toast({
          title: "No active subscription",
          description: "You don't have an active subscription to cancel.",
          variant: "destructive",
        });
        return false;
      }
      
      // Call the subscription cancellation function
      const { data, error } = await supabase.functions.invoke('stripe-subscription/cancel', {
        body: { 
          businessId,
          subscriptionId 
        }
      });
      
      if (error) {
        throw new Error(`Failed to cancel subscription: ${error.message}`);
      }
      
      // Show success message
      toast({
        title: "Subscription Canceled",
        description: "Your subscription has been canceled successfully.",
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    fetchSubscription,
    getSubscriptionDetails,
    cancelSubscription
  };
}
